import { AMOUNT_OWED_TIP } from '../lib/disputeTypes.js';
import {
  buildMatterKpis,
  formatAgingDays,
  formatKpiMoney,
} from '../lib/matterKpis.js';
import FieldTip from './FieldTip.jsx';
import { AgingIcon, DollarIcon, PaidIcon, RemainingIcon } from './kpiIcons.jsx';

/**
 * Collection KPI strip for an existing demand record.
 * Shows Owed, Paid, Remaining, and Aging.
 */
export default function MatterKpiStrip({
  matter,
  paymentTerms = [],
  metricsOpen = false,
  onToggleMetrics,
}) {
  if (!matter?.ROWID && !matter?.id) return null;

  const kpis = buildMatterKpis(matter, { paymentTerms });
  const money = (value) => formatKpiMoney(value, matter);
  const summary = [
    `${money(kpis.remaining)} remaining`,
    kpis.agingDays != null ? `${formatAgingDays(kpis.agingDays)} aging` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <button
        type="button"
        className="debtor-metrics-summary matter-kpi-summary"
        aria-expanded={metricsOpen}
        onClick={() => onToggleMetrics?.(!metricsOpen)}
      >
        <span>{summary}</span>
        <span className="debtor-metrics-summary-toggle">
          {metricsOpen ? 'Hide' : 'KPIs'}
        </span>
      </button>

      <div
        className={
          metricsOpen
            ? 'metrics-bar matter-kpi-strip is-expanded'
            : 'metrics-bar matter-kpi-strip'
        }
        aria-label="Demand collection metrics"
      >
        <div className="matter-kpi-card tone-owed">
          <span className="matter-kpi-icon" aria-hidden="true">
            <DollarIcon />
          </span>
          <span className="matter-kpi-label">
            Owed
            <FieldTip text={AMOUNT_OWED_TIP} />
          </span>
          <strong className="matter-kpi-value">{money(kpis.owed)}</strong>
        </div>

        <div className="matter-kpi-card tone-paid">
          <span className="matter-kpi-icon" aria-hidden="true">
            <PaidIcon />
          </span>
          <span className="matter-kpi-label">Paid</span>
          <strong className="matter-kpi-value">{money(kpis.paid)}</strong>
          <small className="matter-kpi-sub">
            {kpis.owed > 0 ? `${kpis.paidPct}% of owed` : '—'}
          </small>
        </div>

        <div className="matter-kpi-card tone-remaining">
          <span className="matter-kpi-icon" aria-hidden="true">
            <RemainingIcon />
          </span>
          <span className="matter-kpi-label">Remaining</span>
          <strong className="matter-kpi-value">{money(kpis.remaining)}</strong>
          <small className="matter-kpi-sub">
            {kpis.owed > 0 ? `${kpis.remainingPct}% of owed` : '—'}
          </small>
        </div>

        <div
          className={
            kpis.paidFully
              ? 'matter-kpi-card tone-aging is-settled'
              : 'matter-kpi-card tone-aging'
          }
        >
          <span className="matter-kpi-icon" aria-hidden="true">
            <AgingIcon />
          </span>
          <span className="matter-kpi-label">Aging</span>
          <strong className="matter-kpi-value">{formatAgingDays(kpis.agingDays)}</strong>
          <small className="matter-kpi-sub">{kpis.agingLabel}</small>
        </div>
      </div>
    </>
  );
}
